import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { ChangePasswordService } from './service/change-password.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { NotificationsService } from '../../shared/components/notifications/service/notifications.service';
import { PasswordRegexHandler } from '../../core/handlers/password-regex.handler';

@Component({
  selector: 'features-change-password',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.css'
})
export class ChangePasswordComponent implements OnInit {
  token: string | null = null;
  passwordForm!: FormGroup;
  isLoading = false;
  tokenIsValid={status:false,message:""};
  showNewPassword = signal(false);
  showConfirmPassword = signal(false);
  passwordRegex=inject(PasswordRegexHandler);
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private changePasswordService: ChangePasswordService,
    private notificationsService: NotificationsService
  ) { }

  ngOnInit() {
    // Initialize a basic form group to ensure the directive always has a FormGroup
    // It will be replaced with validators when the token is valid
    this.passwordForm = this.fb.group({
      newPassword: [''],
      confirmPassword: ['']
    });
    this.token = this.route.snapshot.queryParamMap.get('token');
    this.validateToken();
  }

  initForm() {
    this.passwordForm = this.fb.group({
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator // At least one uppercase letter and one number
      ]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    return newPassword === confirmPassword ? null : { 'passwordMismatch': true };
  }

  get newPassword() {
    return this.passwordForm?.get('newPassword') ?? null;
  }

  get confirmPassword() {
    return this.passwordForm?.get('confirmPassword') ?? null;
  }

  toggleShowNewPassword() {
    this.showNewPassword.set(!this.showNewPassword());
  }

  toggleShowConfirmPassword() {
    this.showConfirmPassword.set(!this.showConfirmPassword());
  }


  passwordStrengthValidator(control: any) {
    return this.passwordRegex.passwordStrengthValidator(control);
}
  onSubmit() {
    if (this.passwordForm.invalid) {
      return;
    }

    if (!this.token) {
      console.error('No user ID provided');
      this.notificationsService.addNotification({
        message: 'No se pudo identificar el usuario. Por favor, intente nuevamente.',
        type: 'error'
      });
      return;
    }

    this.isLoading = true;

    this.changePasswordService.updatePassword(this.token, this.newPassword?.value)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.notificationsService.addNotification({
            message: 'Contraseña actualizada exitosamente',
            type: 'success',
            duration: 5000
          });
          // Navigate to login after showing success notification
          setTimeout(() => {
            this.router.navigate(['/auth/login']);
          }, 2000);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error updating password:', error);
          this.notificationsService.addNotification({
            message: 'Error al actualizar la contraseña. Por favor, intente nuevamente.',
            type: 'error',
            autoClose: true,
            duration: 7000
          });
        }
      });
  }

  validateToken(){
    if (!this.token) {
      console.error('No user ID provided');
      this.notificationsService.addNotification({
        message: 'No se pudo identificar el usuario. Por favor, intente nuevamente.',
        type: 'error'
      });
      return;
    }
    this.isLoading = true;

    this.changePasswordService.validateToken(this.token)
      .subscribe({
        next:(response)=> {
          this.isLoading = false;
          this.tokenIsValid=response;
          if(response.status){
            this.initForm();
          }else{
            this.notificationsService.addNotification({
              message: response.message,
              type: 'error'
            });
            setTimeout(() => {
              this.router.navigate(['/auth/login']);
            }, 2000);
          }
        },
        error:(error)=> {
          this.isLoading = false;
          this.tokenIsValid=error.error;
          if(error.status==401){
            this.notificationsService.addNotification({
              message: error.error.message,
              type: 'error'
            });
          }
          setTimeout(() => {
            this.router.navigate(['/auth/login']);
          }, 2000);
        }
      });
  }
}
